import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckSquareIcon, HandIcon, MessageSquareIcon } from 'lucide-react';
type PlatformId = 'LinkedIn' | 'X' | 'Instagram' | 'YouTube' | 'TikTok';
const PLATFORMS: {
  id: PlatformId;
  slug: string;
}[] = [{
  id: 'LinkedIn',
  slug: 'linkedin'
}, {
  id: 'X',
  slug: 'x'
}, {
  id: 'Instagram',
  slug: 'instagram'
}, {
  id: 'YouTube',
  slug: 'youtube'
}, {
  id: 'TikTok',
  slug: 'tiktok'
}];

// Content rules are identical across every platform, so they live once here
// and get merged into each platform's column set below.
const CONTENT_ITEMS = ['Blur anything sensitive: customer names, financial data, private messages', 'Your own content from your own Slack', 'No staged or fabricated output', 'No pre-approval needed, post first, submit later'];

// Per-platform Requirements + Limits, matching the reference rules exactly
// (re-skinned to Giga). Content is shared via CONTENT_ITEMS.
const PLATFORM_RULES: Record<PlatformId, {
  requirements: string[];
  limits: string[];
}> = {
  LinkedIn: {
    requirements: ['Disclose the partnership: add #ad at the end of your post (required even if you choose credits)', 'Add your Dub referral link to the post, in the first comment or description (required even if you choose credits)', 'Tag @giga.com in your post', "Include a screenshot or screen recording of Giga's output in Slack or Microsoft Teams", 'Organic reach only, no boosted or promoted posts', 'Real Giga output from a real workspace'],
    limits: ['4 posts per month', '5 days between submissions', '$10,000 cap per post', 'Post must feature Giga, not a multi-tool roundup']
  },
  X: {
    requirements: ["Toggle X's Paid partnership label (required even if you choose credits)", 'Add your Dub referral link to the post, in the first comment or description (required even if you choose credits)', 'Tag @giga__com in your post', "Include a screenshot or screen recording of Giga's output in Slack or Microsoft Teams", 'Organic reach only, no promoted posts', 'Real Giga output from a real workspace'],
    limits: ['12 posts per month', '2 days between submissions', '$10,000 cap per post', 'Post must feature Giga, not a multi-tool roundup']
  },
  Instagram: {
    requirements: ["Toggle Instagram's Paid partnership label and add #ad to the caption (required even if you choose credits)", 'Add your Dub referral link to the post, in your bio or first comment (required even if you choose credits)', 'Tag @meet.giga in your post', "Include a screenshot or screen recording of Giga's output in Slack or Microsoft Teams", 'Organic reach only, no boosted or promoted posts', 'Real Giga output from a real workspace'],
    limits: ['8 posts per month', '3 days between submissions', '$10,000 cap per post', 'Post must feature Giga, not a multi-tool roundup']
  },
  YouTube: {
    requirements: ["Toggle 'Includes paid promotion' in YouTube Studio and disclose verbally or on-screen (required even if you choose credits)", 'Add your Dub referral link in the description (required even if you choose credits)', 'Mention Giga in title or description and link to giga.com', "Include a screen recording or demo of Giga's output in Slack or Microsoft Teams", 'Organic views only, no paid promotion or ads', 'Real Giga output from a real workspace'],
    limits: ['4 videos per month', '5 days between submissions', '$10,000 cap per video', 'Video must feature Giga, not a multi-tool roundup']
  },
  TikTok: {
    requirements: ["Turn on TikTok's 'Disclose video content → Brand organic' toggle and add #ad to the caption (required even if you choose credits)", 'Add your Dub referral link to the post, in your bio or caption (required even if you choose credits)', 'Tag @getgiga in the caption', "Include a screen recording of Giga's output in Slack or Microsoft Teams", 'Organic reach only, no promoted posts or creator marketplace deals', 'Real Giga output from a real workspace'],
    limits: ['8 videos per month', '3 days between submissions', '$10,000 cap per post', 'Video must feature Giga, not a multi-tool roundup']
  }
};
export function CreatorRules() {
  const [activePlatform, setActivePlatform] = useState<PlatformId>('LinkedIn');
  const rules = PLATFORM_RULES[activePlatform];
  const columns: {
    title: string;
    Icon: typeof CheckSquareIcon;
    items: string[];
  }[] = [{
    title: 'Requirements',
    Icon: CheckSquareIcon,
    items: rules.requirements
  }, {
    title: 'Limits',
    Icon: HandIcon,
    items: rules.limits
  }, {
    title: 'Content',
    Icon: MessageSquareIcon,
    items: CONTENT_ITEMS
  }];
  return <section className="py-20 md:py-28 px-6 md:px-8 max-w-7xl mx-auto" data-id="element-2111" data-mp="2epqkbht45l3k">
      <div className="text-center mb-12" data-id="element-2112" data-mp="29rp4d5qzi1wb">
        <h2 className="font-title font-bold text-4xl md:text-6xl tracking-tight mb-4 text-[#2B211A]" data-id="element-2113" data-mp="29qmd2wpr1xfv">
          The rules
        </h2>
        <p className="text-lg text-neutral-500" data-id="element-2114" data-mp="32gw1q1epclfz">Short. Fair. No fine print.</p>
      </div>

      {/* Platform selector */}
      <div className="flex justify-center mb-16" data-id="element-2115" data-mp="3ar3exf4nrh3v">
        <div className="inline-flex items-center gap-1 p-1.5 bg-white rounded-full border border-neutral-200 shadow-sm" data-id="element-2116" data-mp="3erdiym8w3xg3">
          {PLATFORMS.map(({
          id,
          slug
        }) => {
          const active = activePlatform === id;
          return <button key={id} type="button" onClick={() => setActivePlatform(id)} aria-label={id} aria-pressed={active} className={`relative flex items-center justify-center w-16 md:w-24 h-11 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]`} data-id="element-2117" data-mp="3oqcohvyczcyv">
                
                {active && <motion.span layoutId="rules-platform-pill" className="absolute inset-0 rounded-full bg-[#2B211A] shadow-md" transition={{
              type: 'spring',
              stiffness: 400,
              damping: 32
            }} data-id="element-2118" data-mp="6dn45op2q3jl" />}
                <img src={`https://cdn.simpleicons.org/${slug}/${active ? 'ffffff' : '9ca3af'}`} alt={id} className="relative z-10 w-5 h-5 object-contain" loading="lazy" onError={e => {
              const img = e.currentTarget as HTMLImageElement;
              if (!img.dataset.fallback) {
                img.dataset.fallback = '1';
                img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`;
              } else {
                img.style.visibility = 'hidden';
              }
            }} data-id="element-2119" data-mp="l8b3sm7sm7ra" />
                
              </button>;
        })}
        </div>
      </div>

      {/* Three columns */}
      <div className="bg-white rounded-[2.5rem] md:rounded-[3rem] p-8 md:p-14 lg:p-20 border border-neutral-200 shadow-sm" data-id="element-2120" data-mp="9bu2bxjgrpx4">
        <AnimatePresence mode="wait" data-id="element-2121" data-mp="o709inuapus1">
          <motion.div key={activePlatform} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.25
        }} className="grid gap-12 lg:gap-10 md:grid-cols-3" data-id="element-2122" data-mp="ja03ue1cyu1g">
            
            {columns.map(({
            title,
            Icon,
            items
          }, colIndex) => <div key={title} data-id="element-2123" data-mp="15i5ki243k1q2" data-mpi={colIndex}>
                <div className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-[#FCF3EC] mb-8" data-id="element-2124" data-mp="qtsk8sxz7wpr">
                  <span className="flex items-center justify-center w-6 h-6 rounded-md bg-[#EA580C] text-white" data-id="element-2125" data-mp="vslyinl37rqz">
                    <Icon size={14} strokeWidth={2.5} data-id="element-2126" data-mp="hskvb1hco88c" />
                  </span>
                  <span className="font-bold text-[#2B211A]" data-id="element-2127" data-mp="lupm7sgeyqra">{title}</span>
                </div>
                <ul className="space-y-6" data-id="element-2128" data-mp="7uwsrpdi5wfm">
                  {items.map((item, i) => <motion.li key={item} initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                delay: colIndex * 0.06 + i * 0.03
              }} className="flex gap-3 text-[15px] md:text-base text-neutral-600 leading-relaxed" data-id="element-2129" data-mp="7wrayvxe3ag0" data-mpi={i}>
                  
                      <span className="flex-shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded-md bg-[#FCF3EC] text-[#EA580C]" data-id="element-2130" data-mp="tzwaqqodtrin">
                        <Icon size={13} strokeWidth={2.5} data-id="element-2131" data-mp="q1ylxoyix7fs" />
                      </span>
                      <span data-id="element-2132" data-mp="l6rogyvukyzb">{item}</span>
                    </motion.li>)}
                </ul>
              </div>)}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}